import React, { useMemo, useState, useEffect } from "react";
import { Rnd } from "react-rnd";
import {
  X, Minimize2, Maximize2, BarChart3, Grid3x3, LineChart as LineChartIcon,
  Users, Clock, FileStack, ListChecks,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MultiSelect from "@/components/ui/multi-select";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, ReferenceLine, Cell,
} from "recharts";

/**
 * WorkloadModal — Feb 2026
 *
 * Draggable + resizable floating panel that visualises per-user daily
 * workload for the current calendar month. Four metrics (Est Hours /
 * Timesheet Hours / Deliverables / Tasks) + Gantt-style filter chips.
 *
 * Lives at the page level so a PM can open it from any Content Calendar
 * tab (List, Month, Timeline, Gantt, …) and keep comparing numbers
 * while they adjust assignments.
 */

// Palette picked once per render — colours stay stable per user/category
// as long as the input order doesn't change within a single session.
const SERIES_COLOURS = [
  "#6366f1", "#ec4899", "#10b981", "#f59e0b", "#06b6d4", "#8b5cf6",
  "#ef4444", "#14b8a6", "#0ea5e9", "#f97316", "#84cc16", "#a855f7",
  "#eab308", "#22c55e", "#3b82f6", "#f43f5e", "#78716c", "#d946ef",
];

const METRICS = {
  est_hours:        { label: "Est. Hours",     unit: "h",   icon: Clock,       help: "Sum of task est_hours bucketed by task due date and assignee." },
  timesheet_hours:  { label: "Timesheet",      unit: "h",   icon: Clock,       help: "Actual hours booked in timesheets, bucketed by log date and user." },
  deliverable_count:{ label: "Deliverables",   unit: "",    icon: FileStack,   help: "Count of deliverables owned per user per planned date." },
  task_count:       { label: "Task Count",     unit: "",    icon: ListChecks,  help: "Count of tasks assigned to each user, bucketed by task due date." },
};

const ymd = (iso) => (iso || "").slice(0, 10);

// Build the full list of dates in [from, to] inclusive, ISO strings.
const dateRange = (from, to) => {
  if (!from || !to) return [];
  const out = [];
  const d = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  while (d <= end) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return out;
};

// Apply the shared Gantt-style filter chips to the enriched item list.
const applyFilters = (items, f) => (items || []).filter((it) => {
  if (f.clientF.length && !f.clientF.includes(it.client_id)) return false;
  if (f.brandF.length && !f.brandF.includes(it.brand_id)) return false;
  if (f.projectF.length && !f.projectF.includes(it.project_id)
      && !(it.project_names || []).some((n) => f.projectF.includes(n))) return false;
  if (f.packageF.length && !f.packageF.includes(it.package_id)) return false;
  if (f.statusF.length && !f.statusF.includes(it.status)) return false;
  if (f.catF.length && !f.catF.includes(it.category)) return false;
  if (f.ownerF.length && !f.ownerF.includes(it.primary_assignee_user_id)) return false;
  if (f.assigneeF.length) {
    const hits = (it.tasks || []).some((t) => f.assigneeF.includes(t.assignee_id));
    if (!hits) return false;
  }
  return true;
});

/**
 * Compute workload cells + series ordered by total.
 *
 * `metric`:
 *   est_hours        — sum of task.est_hours (1h fallback) per (assignee, due_date)
 *   timesheet_hours  — sum of timesheet entries (passed in) per (user, date)
 *   deliverable_count— count of items grouped by primary owner & planned_date
 *   task_count       — count of linked tasks grouped by assignee & due_date
 *
 * Returned `series` is a list of {id, name, total}. The modal uses it
 * for stacking order, line colouring and the totals strip.
 */
const buildWorkload = (items, from, to, metric, timesheetRows) => {
  const dates = dateRange(from, to);
  const dateSet = new Set(dates);
  const cells = {}; // { [seriesId]: { [date]: value } }
  const names = {}; // { seriesId: displayName }
  const inRange = (d) => d && dateSet.has(d);
  const add = (sid, sname, date, value) => {
    if (!sid || !inRange(date) || !value) return;
    names[sid] = sname || names[sid] || "Unknown";
    cells[sid] = cells[sid] || {};
    cells[sid][date] = (cells[sid][date] || 0) + Number(value || 0);
  };

  if (metric === "est_hours") {
    for (const it of items || []) {
      const tasks = it.tasks || [];
      if (tasks.length > 0) {
        for (const t of tasks) {
          const hours = Number(t.est_hours || 0) || 1;
          add(t.assignee_id, t.assignee_name, ymd(t.due_date), hours);
        }
      } else if (it.primary_assignee_user_id) {
        const hours = Number(it.est_hours || it.effort_hours || 1);
        add(it.primary_assignee_user_id, it.primary_assignee_name, ymd(it.planned_date), hours);
      }
    }
  } else if (metric === "timesheet_hours") {
    // Only count timesheet rows whose task_id belongs to a visible item
    // after the filter chips. We derive the allowed task-id set here and
    // also pre-build an assignee-name lookup from the items so the series
    // labels stay rich even if the timesheet row doesn't carry user_name.
    const taskIds = new Set();
    const taskUser = {}; // task_id -> { user_id, user_name }
    for (const it of items || []) {
      for (const t of (it.tasks || [])) {
        if (t.id) {
          taskIds.add(t.id);
          taskUser[t.id] = { user_id: t.assignee_id, user_name: t.assignee_name };
        }
      }
    }
    for (const r of (timesheetRows || [])) {
      if (!taskIds.has(r.task_id)) continue;
      const uname = r.user_name || taskUser[r.task_id]?.user_name;
      add(r.user_id, uname, r.date, r.hours);
    }
  } else if (metric === "deliverable_count") {
    // One deliverable → +1 for its primary owner on its planned date.
    for (const it of items || []) {
      const uid = it.primary_assignee_user_id || "_unassigned";
      const uname = it.primary_assignee_name || "Unassigned";
      add(uid, uname, ymd(it.planned_date), 1);
    }
  } else if (metric === "task_count") {
    // One task → +1 for the task assignee on its due date.
    for (const it of items || []) {
      for (const t of (it.tasks || [])) {
        const uid = t.assignee_id || "_unassigned";
        const uname = t.assignee_name || "Unassigned";
        add(uid, uname, ymd(t.due_date), 1);
      }
    }
  }

  const series = Object.keys(cells).map((sid) => ({
    id: sid, name: names[sid] || "Unknown",
    total: Object.values(cells[sid]).reduce((s, v) => s + v, 0),
  })).sort((a, b) => b.total - a.total);
  return { cells, series, dates };
};

const SeriesDot = ({ color }) => (
  <span className="inline-block w-2.5 h-2.5 rounded-full align-middle"
        style={{ backgroundColor: color }} aria-hidden="true" />
);

const fmtVal = (v, unit) => {
  if (!v) return "";
  if (unit === "h") return Number(v).toFixed(1);
  return String(Math.round(v));
};

// ───────── Stacked bar view ─────────
function StackedBarView({ work, colourFor, unit, capacity, overCells }) {
  const data = useMemo(() => work.dates.map((d) => {
    const row = { date: d.slice(5), _total: 0 };
    work.series.forEach((s) => {
      const v = unit === "h"
        ? Number((work.cells[s.id]?.[d] || 0).toFixed(1))
        : Math.round(work.cells[s.id]?.[d] || 0);
      row[s.id] = v;
      row._total += v;
    });
    row._over = capacity > 0 && row._total > capacity;
    return row;
  }), [work, unit, capacity]);
  if (work.series.length === 0) return <EmptyState />;
  return (
    <div className="h-full w-full" data-testid="workload-view-bar">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 16, bottom: 24, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" />
          <YAxis tick={{ fontSize: 10 }}
                  label={{ value: unit === "h" ? "Hours" : "Count", angle: -90, position: "insideLeft", fontSize: 10 }} />
          <Tooltip wrapperStyle={{ fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {/* Iter · Feb 2026 — Capacity reference line. Rendered only when
              the metric reports hours AND a positive capacity is set. */}
          {unit === "h" && capacity > 0 && (
            <ReferenceLine y={capacity} stroke="#ef4444" strokeDasharray="4 2"
                            label={{ value: `Capacity ${capacity}h`, position: "right", fill: "#ef4444", fontSize: 10 }} />
          )}
          {work.series.map((s) => (
            <Bar key={s.id} dataKey={s.id} name={s.name} stackId="w" fill={colourFor(s.id)} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {unit === "h" && capacity > 0 && overCells > 0 && (
        <div className="absolute right-3 top-1 text-[10px] font-semibold text-rose-600 bg-background/80 px-1.5 py-0.5 rounded"
             data-testid="workload-overcap-badge">
          ⚠ {overCells} overloaded day{overCells === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}

// ───────── Heatmap view ─────────
function HeatmapView({ work, colourFor, unit, capacity }) {
  const maxCell = useMemo(() => {
    let m = 0;
    for (const sid of Object.keys(work.cells)) {
      for (const d of Object.keys(work.cells[sid])) {
        if (work.cells[sid][d] > m) m = work.cells[sid][d];
      }
    }
    return m || 1;
  }, [work]);
  if (work.series.length === 0) return <EmptyState />;
  const colW = 22;
  const over = (val) => unit === "h" && capacity > 0 && val > capacity;
  return (
    <div className="h-full w-full overflow-auto" data-testid="workload-view-heatmap">
      <table className="text-[10px] border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th className="sticky left-0 bg-background text-left pr-2 min-w-[120px]">Series</th>
            {work.dates.map((d) => (
              <th key={d} className="font-mono text-muted-foreground text-center"
                  style={{ width: colW, minWidth: colW }} title={d}>
                {d.slice(8)}
              </th>
            ))}
            <th className="sticky right-0 bg-background pl-2 text-right min-w-[48px]">Σ</th>
          </tr>
        </thead>
        <tbody>
          {work.series.map((s) => (
            <tr key={s.id}>
              <td className="sticky left-0 bg-background pr-2 whitespace-nowrap">
                <SeriesDot color={colourFor(s.id)} />
                <span className="ml-1.5">{s.name}</span>
              </td>
              {work.dates.map((d) => {
                const h = work.cells[s.id]?.[d] || 0;
                const alpha = h > 0 ? Math.min(1, 0.15 + (h / maxCell) * 0.85) : 0;
                const isOver = over(h);
                return (
                  <td key={d}
                      className="text-center font-mono"
                      style={{
                        width: colW, minWidth: colW, height: 22,
                        // Overloaded cells go solid red regardless of
                        // the user colour so hotspots jump out visually.
                        backgroundColor: isOver ? "#dc2626" : (h > 0 ? colourFor(s.id) : "transparent"),
                        opacity: isOver ? 1 : (h > 0 ? alpha : 1),
                        color: isOver ? "#fff" : (alpha > 0.55 ? "#fff" : undefined),
                        outline: isOver ? "1px solid #991b1b" : undefined,
                        fontWeight: isOver ? 700 : undefined,
                      }}
                      title={`${s.name} · ${d} · ${fmtVal(h, unit)}${unit}${isOver ? " · OVER CAPACITY" : ""}`}
                      data-testid={`workload-cell-${s.id}-${d}`}>
                    {h > 0 ? (unit === "h" ? Math.round(h) : h) : ""}
                  </td>
                );
              })}
              <td className="sticky right-0 bg-background pl-2 text-right font-mono font-semibold">
                {unit === "h" ? s.total.toFixed(1) : Math.round(s.total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────── Line chart view ─────────
function LineChartView({ work, colourFor, unit, capacity }) {
  const data = useMemo(() => work.dates.map((d) => {
    const row = { date: d.slice(5) };
    work.series.forEach((s) => {
      row[s.id] = unit === "h"
        ? Number((work.cells[s.id]?.[d] || 0).toFixed(1))
        : Math.round(work.cells[s.id]?.[d] || 0);
    });
    return row;
  }), [work, unit]);
  if (work.series.length === 0) return <EmptyState />;
  return (
    <div className="h-full w-full" data-testid="workload-view-line">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 16, bottom: 24, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" />
          <YAxis tick={{ fontSize: 10 }}
                  label={{ value: unit === "h" ? "Hours" : "Count", angle: -90, position: "insideLeft", fontSize: 10 }} />
          <Tooltip wrapperStyle={{ fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {unit === "h" && capacity > 0 && (
            <ReferenceLine y={capacity} stroke="#ef4444" strokeDasharray="4 2"
                            label={{ value: `Capacity ${capacity}h`, position: "right", fill: "#ef4444", fontSize: 10 }} />
          )}
          {work.series.map((s) => (
            <Line key={s.id} type="monotone" dataKey={s.id} name={s.name}
                  stroke={colourFor(s.id)} strokeWidth={2} dot={{ r: 2 }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground"
         data-testid="workload-empty">
      <div className="text-center">
        <Users className="w-8 h-8 mx-auto mb-2 opacity-60" />
        <div className="font-semibold">No workload data for this range</div>
        <div className="text-xs mt-1">Try a different metric, widen the date range, or clear the filters.</div>
      </div>
    </div>
  );
}

export default function WorkloadModal({
  open, onClose, items, month,
  brands = [], projects = [], packages = [], members = [],
  statuses = [],
  // CATEGORIES list imported from Packages — pass explicit list from
  // parent so we don't fork the enum.
  categories = [],
  clients = [],
}) {
  const { activeWorkspaceId: wsId } = useAuth();
  const [pos, setPos] = useState(() => {
    const base = { x: Math.max(40, (window.innerWidth || 1200) - 1080 - 40),
                   y: 90, w: 1080, h: 680 };
    try {
      const saved = JSON.parse(localStorage.getItem("cc.workloadPos") || "null");
      if (saved && saved.x != null) return { ...base, ...saved };
    } catch { /* ignore */ }
    return base;
  });
  const [minimized, setMinimized] = useState(false);
  const [view, setView] = useState("bar");       // bar | heatmap | line
  const [metric, setMetric] = useState("est_hours");
  // Iter · Feb 2026 — Daily capacity (hours) per user. Defaults to 8h,
  // persisted in localStorage so a PM's chosen cap sticks across sessions.
  // Used by the Bar/Line charts to render a red threshold line and by the
  // Heatmap to repaint overloaded cells solid red.
  const [capacity, setCapacity] = useState(() => {
    const n = parseFloat(localStorage.getItem("cc.workloadCapacity") || "");
    return Number.isFinite(n) && n >= 0 ? n : 8;
  });
  const setCapacityPersist = (val) => {
    setCapacity(val);
    try { localStorage.setItem("cc.workloadCapacity", String(val)); } catch { /* ignore */ }
  };

  const [range, setRange] = useState(() => defaultRange(month));
  useEffect(() => { setRange(defaultRange(month)); }, [month]);

  // Filter chips (same shape the Gantt uses).
  const [clientF, setClientF] = useState([]);
  const [brandF, setBrandF] = useState([]);
  const [projectF, setProjectF] = useState([]);
  const [packageF, setPackageF] = useState([]);
  const [ownerF, setOwnerF] = useState([]);
  const [assigneeF, setAssigneeF] = useState([]);
  const [statusF, setStatusF] = useState([]);
  const [catF, setCatF] = useState([]);
  const resetFilters = () => {
    setClientF([]); setBrandF([]); setProjectF([]); setPackageF([]);
    setOwnerF([]); setAssigneeF([]); setStatusF([]); setCatF([]);
  };
  const filterCount = clientF.length + brandF.length + projectF.length
    + packageF.length + ownerF.length + assigneeF.length
    + statusF.length + catF.length;

  // Backend timesheet rows for the "Timesheet" metric. Fetched only
  // when the Timesheet metric is active AND the modal is open — any
  // other metric is pure client-side so we skip the request entirely.
  const [tsRows, setTsRows] = useState([]);
  const [tsLoading, setTsLoading] = useState(false);
  useEffect(() => {
    if (!open || metric !== "timesheet_hours" || !wsId) { setTsRows([]); return; }
    let cancelled = false;
    setTsLoading(true);
    api.get(`/workspaces/${wsId}/content-calendar/workload-timesheet`,
            { params: { from_: range.from, to: range.to } })
      .then((r) => { if (!cancelled) setTsRows(r.data?.rows || []); })
      .catch(() => { if (!cancelled) setTsRows([]); })
      .finally(() => { if (!cancelled) setTsLoading(false); });
    return () => { cancelled = true; };
  }, [open, metric, wsId, range.from, range.to]);

  const savePos = (next) => {
    try { localStorage.setItem("cc.workloadPos", JSON.stringify(next)); } catch { /* ignore */ }
  };

  const filtered = useMemo(
    () => applyFilters(items || [], { clientF, brandF, projectF, packageF, ownerF, assigneeF, statusF, catF }),
    [items, clientF, brandF, projectF, packageF, ownerF, assigneeF, statusF, catF],
  );
  const work = useMemo(
    () => buildWorkload(filtered, range.from, range.to, metric, tsRows),
    [filtered, range.from, range.to, metric, tsRows],
  );
  const colourFor = useMemo(() => {
    const map = {};
    work.series.forEach((s, i) => { map[s.id] = SERIES_COLOURS[i % SERIES_COLOURS.length]; });
    return (sid) => map[sid] || "#64748b";
  }, [work.series]);

  // Count (user × date) cells that bust the daily capacity. Used by the
  // bar view's badge + the header, and skipped altogether for non-hour
  // metrics where capacity is irrelevant.
  const overCells = useMemo(() => {
    if (METRICS[metric].unit !== "h" || capacity <= 0) return 0;
    let n = 0;
    for (const sid of Object.keys(work.cells)) {
      for (const d of Object.keys(work.cells[sid])) {
        if (work.cells[sid][d] > capacity) n += 1;
      }
    }
    return n;
  }, [work, capacity, metric]);

  if (!open) return null;

  const unit = METRICS[metric].unit;
  const totalAll = work.series.reduce((s, u) => s + u.total, 0);
  const headerH = 44;
  // header + metric strip + filter strip
  const chromeH = 44 + 44 + 44;
  const bodyH = minimized ? 0 : pos.h - headerH - chromeH;

  return (
    <Rnd
      size={{ width: pos.w, height: minimized ? headerH : pos.h }}
      position={{ x: pos.x, y: pos.y }}
      minWidth={580}
      minHeight={minimized ? headerH : 420}
      maxWidth={1700}
      maxHeight={1000}
      bounds="window"
      dragHandleClassName="workload-drag-handle"
      cancel=".workload-cancel"
      onDragStop={(_, d) => { const n = { ...pos, x: d.x, y: d.y }; setPos(n); savePos(n); }}
      onResizeStop={(_, __, ref, ___, position) => {
        const n = {
          x: position.x, y: position.y,
          w: parseInt(ref.style.width, 10),
          h: minimized ? pos.h : parseInt(ref.style.height, 10),
        };
        setPos(n); savePos(n);
      }}
      enableResizing={!minimized}
      style={{ zIndex: 1000 }}
      data-testid="workload-modal"
    >
      <div className="h-full w-full flex flex-col border border-border/80 bg-background overflow-hidden rounded-none"
           style={{ boxShadow: "0 24px 60px -12px rgba(0,0,0,0.35), 0 8px 16px -4px rgba(0,0,0,0.15)" }}>
        {/* Header */}
        <div className="workload-drag-handle h-11 shrink-0 px-3 flex items-center gap-2 bg-muted/60 border-b border-border/60 cursor-move select-none">
          <BarChart3 className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">Workload</span>
          <span className="text-[11px] text-muted-foreground ml-1" data-testid="workload-header-range">
            {range.from} → {range.to}
          </span>
          {tsLoading && (
            <span className="text-[10px] text-primary animate-pulse ml-2">loading…</span>
          )}
          <span className="text-[11px] text-muted-foreground ml-auto" data-testid="workload-header-totals">
            {work.series.length} {metric === "deliverable_count" ? "owner" : metric === "task_count" ? "assignee" : "user"}
            {work.series.length === 1 ? "" : "s"} · {" "}
            {unit === "h" ? totalAll.toFixed(1) : Math.round(totalAll)}{unit} total
            {overCells > 0 && (
              <span className="ml-2 text-rose-600 font-semibold" data-testid="workload-header-over">
                · {overCells} over cap
              </span>
            )}
          </span>
          <button type="button"
                  className="workload-cancel w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
                  onClick={() => setMinimized((m) => !m)}
                  title={minimized ? "Restore" : "Minimize"}
                  data-testid="workload-minimize">
            {minimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
          </button>
          <button type="button"
                  className="workload-cancel w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
                  onClick={onClose} title="Close" data-testid="workload-close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {!minimized && (
          <>
            {/* Metric + view strip */}
            <div className="workload-cancel shrink-0 px-3 py-2 flex items-center gap-2 border-b border-border/60 bg-background flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mr-1">Metric</span>
              <div className="flex items-center border border-border rounded-none">
                {Object.entries(METRICS).map(([key, m]) => {
                  const Icon = m.icon;
                  const active = metric === key;
                  return (
                    <button key={key} type="button"
                            onClick={() => setMetric(key)}
                            className={`h-8 px-2.5 text-xs flex items-center gap-1 border-l first:border-l-0 border-border ${
                              active ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted/60 text-muted-foreground"
                            }`}
                            title={m.help}
                            data-testid={`workload-metric-${key}`}>
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden md:inline">{m.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-end gap-1.5 ml-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">From</Label>
                  <Input type="date" value={range.from}
                         onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                         className="h-8 w-[140px] text-xs rounded-none"
                         data-testid="workload-date-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">To</Label>
                  <Input type="date" value={range.to}
                         onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                         className="h-8 w-[140px] text-xs rounded-none"
                         data-testid="workload-date-to" />
                </div>
                <Button variant="outline" size="sm" className="h-8 rounded-none text-xs"
                        onClick={() => setRange(defaultRange(month))}
                        data-testid="workload-reset-range">
                  Month
                </Button>
              </div>
              {/* Daily capacity input — hours per user per day. Only
                  meaningful when the metric reports hours. */}
              <div className="flex items-end gap-1 ml-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Cap · hrs/day</Label>
                  <Input type="number" min="0" step="0.5" value={capacity}
                         onChange={(e) => setCapacityPersist(Math.max(0, parseFloat(e.target.value) || 0))}
                         className="h-8 w-[90px] text-xs rounded-none"
                         disabled={unit !== "h"}
                         title={unit === "h" ? "Overloaded days show in red" : "Only applies to Hours metrics"}
                         data-testid="workload-capacity-input" />
                </div>
              </div>
              <div className="ml-auto flex items-center border border-border rounded-none">
                <ViewBtn active={view === "bar"} onClick={() => setView("bar")} icon={<BarChart3 className="w-3.5 h-3.5" />} label="Stacked" testid="workload-view-bar-btn" />
                <ViewBtn active={view === "heatmap"} onClick={() => setView("heatmap")} icon={<Grid3x3 className="w-3.5 h-3.5" />} label="Heatmap" testid="workload-view-heatmap-btn" />
                <ViewBtn active={view === "line"} onClick={() => setView("line")} icon={<LineChartIcon className="w-3.5 h-3.5" />} label="Trend" testid="workload-view-line-btn" />
              </div>
            </div>

            {/* Filter chips (Gantt-style). Iter · Feb 2026 — wraps
                instead of horizontal-scrolling so the MultiSelect
                popovers don't get clipped by `overflow-x-auto`. */}
            <div className="workload-cancel shrink-0 px-3 py-2 flex items-center gap-1.5 border-b border-border/60 bg-muted/20 flex-wrap">
              <MultiSelect options={clients.map((c) => ({ value: c.id, label: c.name }))}
                           value={clientF} onChange={setClientF} allLabel="All clients" placeholder="Client" width="w-36" testid="workload-filter-client" />
              <MultiSelect options={brands.map((b) => ({ value: b.id, label: b.name }))}
                           value={brandF} onChange={setBrandF} allLabel="All brands" placeholder="Brand" width="w-36" testid="workload-filter-brand" />
              <MultiSelect options={projects.map((p) => ({ value: p.id, label: p.name }))}
                           value={projectF} onChange={setProjectF} allLabel="All projects" placeholder="Project" width="w-36" testid="workload-filter-project" />
              <MultiSelect options={packages.map((p) => ({ value: p.id, label: p.name }))}
                           value={packageF} onChange={setPackageF} allLabel="All packages" placeholder="Package" width="w-36" testid="workload-filter-package" />
              <MultiSelect options={members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email }))}
                           value={ownerF} onChange={setOwnerF} allLabel="All owners" placeholder="Owner" width="w-36" testid="workload-filter-owner" />
              <MultiSelect options={members.map((m) => ({ value: m.user_id || m.id, label: m.name || m.email }))}
                           value={assigneeF} onChange={setAssigneeF} allLabel="All assignees" placeholder="Assignee" width="w-36" testid="workload-filter-assignee" />
              <MultiSelect options={statuses.map((s) => ({ value: s, label: s.replaceAll("_", " ") }))}
                           value={statusF} onChange={setStatusF} allLabel="All statuses" placeholder="Status" width="w-32" testid="workload-filter-status" />
              <MultiSelect options={categories.map((c) => ({ value: c, label: c }))}
                           value={catF} onChange={setCatF} allLabel="All categories" placeholder="Category" width="w-32" testid="workload-filter-category" />
              {filterCount > 0 && (
                <button type="button"
                        onClick={resetFilters}
                        className="text-[10px] text-primary hover:underline whitespace-nowrap pl-2"
                        data-testid="workload-filter-reset">
                  Clear ({filterCount})
                </button>
              )}
              <span className="ml-auto text-[10px] text-muted-foreground whitespace-nowrap pl-2">
                {filtered.length} / {(items || []).length} deliverable{(items || []).length === 1 ? "" : "s"}
              </span>
            </div>

            {/* Chart body */}
            <div className="flex-1 min-h-0 p-3 relative" style={{ height: Math.max(200, bodyH) }}>
              {view === "bar" && <StackedBarView work={work} colourFor={colourFor} unit={unit} capacity={capacity} overCells={overCells} />}
              {view === "heatmap" && <HeatmapView work={work} colourFor={colourFor} unit={unit} capacity={capacity} />}
              {view === "line" && <LineChartView work={work} colourFor={colourFor} unit={unit} capacity={capacity} />}
            </div>

            {/* Series totals strip */}
            <div className="workload-cancel shrink-0 border-t border-border/60 bg-muted/30 px-3 py-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] overflow-x-auto"
                 data-testid="workload-totals-strip">
              {work.series.length === 0 && (
                <span className="text-muted-foreground">No data for the selected metric in this range.</span>
              )}
              {work.series.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1.5" data-testid={`workload-total-${s.id}`}>
                  <SeriesDot color={colourFor(s.id)} />
                  <span className="font-medium">{s.name}</span>
                  <span className="font-mono text-muted-foreground">
                    {unit === "h" ? s.total.toFixed(1) : Math.round(s.total)}{unit}
                  </span>
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </Rnd>
  );
}

function ViewBtn({ active, onClick, icon, label, testid }) {
  return (
    <button type="button"
            onClick={onClick}
            className={`h-8 px-2.5 text-xs flex items-center gap-1 border-l first:border-l-0 border-border ${
              active ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted/60 text-muted-foreground"
            }`}
            data-testid={testid}>
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function defaultRange(month) {
  const [yy, mm] = (month || "").split("-").map(Number);
  if (!yy || !mm) {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
  }
  const first = new Date(yy, mm - 1, 1);
  const last = new Date(yy, mm, 0);
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}
